/**
 * Platform detection — for keyboard-shortcut LABELS only.
 *
 * The handlers themselves accept `metaKey || ctrlKey` everywhere and must keep
 * doing so: a Mac user on an external PC keyboard presses Ctrl, and the browser
 * reports Meta for the Windows key on some Linux WMs. Only the hints were
 * wrong — they hardcoded ⌘, which is a lie on Windows and Linux, where the
 * agent then hunts for a key their keyboard doesn't have.
 */

/**
 * True on macOS (and iPadOS, which reports as a Mac). Best-effort: userAgentData
 * where the browser has it, then the deprecated navigator.platform, then the UA
 * string. Never throws — this runs at module load, including in a non-browser
 * (test/SSR) context where `navigator` is undefined.
 */
export const IS_MAC: boolean = (() => {
  try {
    if (typeof navigator === 'undefined') return false;
    const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
    const platform = uaData?.platform || navigator.platform || '';
    if (platform) return /mac/i.test(platform);
    return /mac/i.test(navigator.userAgent || '');
  } catch {
    return false; // exotic environment — assume the PC label, which is readable everywhere
  }
})();

/** The modifier to SHOW in a hint: '⌘' on Mac, 'Ctrl' everywhere else. */
export const MOD_KEY: string = IS_MAC ? '⌘' : 'Ctrl';

/**
 * Does this event carry the "primary modifier"? Deliberately platform-blind —
 * both keys work on every OS. Exported so the hint and the handler can't drift.
 */
export function isModKey(e: { metaKey?: boolean; ctrlKey?: boolean }): boolean {
  return !!(e.metaKey || e.ctrlKey);
}
